import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IconDefinition, faExclamationTriangle } from '@fortawesome/pro-duotone-svg-icons';
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons';
import { Store } from '@ngxs/store';
import { WindowState } from '@progress/kendo-angular-dialog';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { BasicBrickComponent } from 'src/app/components/bricks/basic-brick-component';
import { SamwebWindowComponent } from 'src/app/components/utility/samweb-window/samweb-window.component';
import { emailArrayToString } from 'src/app/helper/emailingHelpers';
import { MessageActions } from 'src/app/samwebDescription/samweb/messages/MessageActions';
import { ShortcutDescription } from 'src/app/samwebDescription/samweb/shortcuts/shortcutDescription';
import { LongRunningContextCategoryTyp } from 'src/app/samwebDescription/samweb/waitingContext/waitingContextCategory';
import { Email } from 'src/app/store/samwin/email/email-behaviour/email';
import { EmailBehaviourState } from 'src/app/store/samwin/email/email-behaviour/email-behaviour.state';
import { EndEmailProcess } from 'src/app/store/samwin/email/email-dialog/email-dialog-behaviour.actions';
import { EmailDialogSelectors } from 'src/app/store/samwin/email/email-dialog/email-dialog-behaviour.selectors';
import { MessageInfo } from 'src/app/store/samwin/samwin-mail-list/message-info';
import { MarkMessageUndone, ReassignMessage } from 'src/app/store/samwin/samwin-mail-list/samwin-message-list.actions';
import { SamwinMessageListStateSelectors } from 'src/app/store/samwin/samwin-mail-list/samwin-message-list.selectors';
import { MailingFolderEnum } from 'src/app/store/samwin/samwin-skills/mailing-folders-enum';
import { HOTKEYS_DISABLED } from 'src/app/store/samwin/shortcut-behaviour/hotkeys-disabled';
import { LongRunningContextState } from 'src/app/store/server-communication/running-actions/long-running-context.state';
import { SamwebWindowState, WindowType, WindowTypes } from 'src/app/store/utility/samweb-window/samweb-window.state';
import { AttachmentRemoteDto } from 'src/generated/swagger';

@Component({
   selector: 'samweb-email-dialog',
   templateUrl: './email-dialog.component.html',
   styleUrls: ['./email-dialog.component.scss'],
})
export class EmailDialogComponent extends BasicBrickComponent implements OnInit, OnDestroy {
   @ViewChild('emailDialog', { static: true })
   public emailDialog;

   public windowState: WindowState = 'default';

   @Input()
   public messageId: string;

   public type: WindowType = WindowTypes.Email;

   public envelope: IconDefinition = faEnvelope;

   public email$: Observable<Email>;

   public emailRecipient$: Observable<string>;

   public attachments$: Observable<AttachmentRemoteDto[]>;

   public isEditable$: Observable<boolean>;

   public messageInfo$: Observable<MessageInfo>;

   public showToInHeader: boolean;

   public isLoading$: Observable<boolean>;

   public hasReferenceMail$: Observable<boolean>;

   public isOpen$: Observable<boolean>;

   public windowState$: Observable<WindowState>;

   public displayCloseDialog = false;

   public isAttachmentUploadCanceled = false;

   private destroy$ = new Subject();

   public closeDialogMargin = { horizontal: -3, vertical: 3 };

   public warningIcon = faExclamationTriangle;

   public completeAction: MessageActions = MessageActions.COMPLETE;
   public trackAction: MessageActions = MessageActions.TRACK;
   public deleteAction: MessageActions = MessageActions.DELETE;

   public inboxFolder: string = MailingFolderEnum.INBOX.name;
   public completeFolder: string = MailingFolderEnum.COMPLETED.name;
   public trackFolder: string = MailingFolderEnum.TRACKING.name;
   public deleteFolder: string = MailingFolderEnum.DELETED.name;

   @ViewChild(SamwebWindowComponent) window: SamwebWindowComponent;

   @ViewChild('closeTooltipDirective')
   public closeTooltipDirective: TooltipDirective;

   constructor(private store: Store, private cdr: ChangeDetectorRef) {
      super();
   }

   public keyActions: ShortcutDescription[] = [
      {
         shortcutKey: HOTKEYS_DISABLED,
         action: () => {
            return true;
         },
      },
   ];

   ngOnInit() {
      this.email$ = this.store.select(EmailBehaviourState.getEmail).pipe(map((getMail) => getMail(this.messageId)));

      this.emailRecipient$ = this.email$.pipe(
         map((email) => email?.to.map((contact) => (!_.isEmpty(contact.name) ? contact.name : contact.email))),
         map((to) => emailArrayToString(to, '; '))
      );

      this.attachments$ = this.store.select(EmailBehaviourState.getAttachments).pipe(map((getMail) => getMail(this.messageId)));
      this.isEditable$ = this.store.select(EmailDialogSelectors.isStateContainingInstance).pipe(map((containsInstance) => containsInstance(this.messageId)));
      this.isOpen$ = this.store.select(SamwebWindowState.isOpen).pipe(map((isOpen) => isOpen(this.type, this.messageId)));
      this.windowState$ = this.store.select(SamwebWindowState.getWindowState).pipe(map((windowState) => windowState(this.type, this.messageId)));
      this.messageInfo$ = this.store.select(SamwinMessageListStateSelectors.getMessageById).pipe(map((getMessageById) => getMessageById(this.messageId)));
      this.isLoading$ = this.store
         .select(LongRunningContextState.isLongRunningContextInProgressForCategory)
         .pipe(map((getContext) => getContext(LongRunningContextCategoryTyp.EMAIL_WINDOW, this.messageId)));
      this.hasReferenceMail$ = this.store.select(EmailDialogSelectors.hasReferenceMail).pipe(map((hasReferenceMail) => hasReferenceMail(this.messageId)));

      this.messageInfo$
         .pipe(
            tap((messageInfo) => {
               const folder = messageInfo?.group;
               this.showToInHeader = folder === MailingFolderEnum.OUTBOX.name || folder === MailingFolderEnum.SENT.name;
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   onCancel(messageInfo: MessageInfo): void {
      if (this.isAttachmentUploadCanceled) {
         this.isAttachmentUploadCanceled = false;
         return;
      }
      if (!(messageInfo?.group === MailingFolderEnum.INBOX.name)) {
         this.store.dispatch(new EndEmailProcess(this.messageId));
         return;
      }
      if (messageInfo?.skill.skillConfiguration.isShared) {
         this.store.dispatch(new MarkMessageUndone(messageInfo.callId, messageInfo.messageId));
         return;
      }
      if (!_.isNil(this.window.closePopupControl)) {
         this.closeTooltipDirective.show(this.window.closePopupControl.nativeElement);
         return;
      }
   }

   onCloseDialogDone(messageInfo: MessageInfo): void {
      if (messageInfo?.group === MailingFolderEnum.INBOX.name) {
         MessageActions.COMPLETE.executeAction$(this.store, messageInfo);
      }
   }

   onCloseDialogReassign(messageInfo: MessageInfo): void {
      if (messageInfo?.group === MailingFolderEnum.INBOX.name) {
         this.store.dispatch(new ReassignMessage(messageInfo.callId, messageInfo.messageId));
      }
   }

   onCloseDialogCancel(messageInfo: MessageInfo): void {
      if (messageInfo?.group === MailingFolderEnum.INBOX.name) {
         this.closeTooltipDirective.hide();
      }
   }

   attachmentUploadCanceled(value: boolean) {
      this.isAttachmentUploadCanceled = value;
   }
}
